import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import JSZip from "jszip";
import { useStore } from "@/lib/store";
import { apiRequest } from "@/lib/queryClient";
import { Sparkles, Download, Loader2, CheckCircle2 } from "lucide-react";

export type RenderedFrame = {
  index: number;
  role: string;
  r2Key: string;
  signedUrl: string;
  graphicId: string;
  /** VP-230 4b: surowe tło (bez tekstu) — do podglądu WYSIWYG w kartach klatek. */
  backgroundR2Key?: string;
  backgroundUrl?: string;
};

type RenderJobResponse = {
  jobId: string;
  status: "queued" | "running" | "done" | "failed";
  progress?: number;
  frames?: RenderedFrame[];
  meta?: { tokensDeducted: number; tokensRemaining: number };
  error?: string;
};

type Props = {
  conversationId: string;
  messageId: string;
  /** When provided, sent to backend as the script-of-record for this render
   *  instead of re-parsing the assistant message. Used by the edit-frame UI. */
  editedScript?: unknown;
  /** VP-230 4b: powiadamia rodzica o gotowych klatkach (m.in. backgroundUrl),
   *  żeby karty podglądu mogły podmienić placeholder na realne tło. */
  onFramesRendered?: (frames: RenderedFrame[]) => void;
};

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes hard stop
// Poll requests are short GET calls — 10s is ample; prevents apiRequest's
// default timeout from surfacing a false error while the job is still running.
const POLL_REQUEST_TIMEOUT_MS = 10_000;

export default function RenderStoriesButton({
  conversationId,
  messageId,
  editedScript,
  onFramesRendered,
}: Props) {
  const { t } = useTranslation();
  const {
    storiesBackgroundMode,
    storiesImageProvider,
    storiesUserBackgroundR2Key,
    storiesUserBackgroundR2Keys,
    storiesUserBackgroundMode,
    storiesUserBackgroundAssignments,
  } = useStore();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [frames, setFrames] = useState<RenderedFrame[] | null>(null);
  const [meta, setMeta] = useState<{ tokensDeducted: number; tokensRemaining: number } | null>(
    null
  );

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartRef = useRef<number>(0);

  const cancelPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // Clean up polling on unmount
  useEffect(() => {
    return cancelPolling;
  }, []);

  const pollJob = (jobId: string) => {
    if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
      setError(
        t("storiesCreator.render.timeout", "Przekroczono czas oczekiwania — spróbuj ponownie.")
      );
      setLoading(false);
      return;
    }

    pollTimerRef.current = setTimeout(async () => {
      try {
        const job = (await apiRequest(
          "GET",
          `/api/stories-creator/render-jobs/${jobId}`,
          undefined,
          { timeoutMs: POLL_REQUEST_TIMEOUT_MS }
        )) as RenderJobResponse;

        if (job.status === "done") {
          setFrames(job.frames ?? []);
          setMeta(job.meta ?? null);
          setProgress(100);
          setLoading(false);
          if (job.frames && job.frames.length > 0) onFramesRendered?.(job.frames);
        } else if (job.status === "failed") {
          setError(job.error ?? t("storiesCreator.render.unknownError", "Błąd renderowania"));
          setLoading(false);
        } else {
          // queued | running — keep polling
          setProgress(job.progress ?? 0);
          pollJob(jobId);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      }
    }, POLL_INTERVAL_MS);
  };

  const handleRender = async () => {
    // Cancel any leftover poll chain from a previous render attempt.
    cancelPolling();

    setLoading(true);
    setError(null);
    setProgress(0);
    setFrames(null);
    setMeta(null);

    try {
      const keys =
        storiesUserBackgroundR2Keys.length > 0
          ? storiesUserBackgroundR2Keys
          : storiesUserBackgroundR2Key
            ? [storiesUserBackgroundR2Key]
            : [];

      const res = (await apiRequest(
        "POST",
        `/api/stories-creator/conversations/${conversationId}/render`,
        {
          messageId,
          backgroundMode: storiesBackgroundMode,
          imageProvider: storiesImageProvider,
          userBackgroundR2Key: keys[0],
          userBackgroundR2Keys: keys.length > 0 ? keys : undefined,
          userBackgroundMode: keys.length > 0 ? storiesUserBackgroundMode : undefined,
          userBackgroundAssignments:
            keys.length > 0 && Object.keys(storiesUserBackgroundAssignments).length > 0
              ? storiesUserBackgroundAssignments
              : undefined,
          editedScript,
        }
      )) as { jobId: string };

      pollStartRef.current = Date.now();
      pollJob(res.jobId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      setLoading(false);
    }
  };

  const downloadAll = async () => {
    if (!frames) return;
    const zip = new JSZip();
    try {
      await Promise.all(
        frames.map(async (f) => {
          const res = await fetch(f.signedUrl);
          if (!res.ok) throw new Error(`Failed to download frame ${f.index} (HTTP ${res.status})`);
          const blob = await res.blob();
          zip.file(`story-frame-${f.index}-${f.role}.png`, blob);
        })
      );
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      try {
        const link = document.createElement("a");
        link.href = url;
        link.download = "stories.zip";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (frames) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-emerald-400 text-[11px]">
          <CheckCircle2 size={14} />
          <span>
            {t("storiesCreator.render.success", "Wygenerowano!")} ({frames.length})
          </span>
          {meta && (
            <span className="text-white/40">
              · -{meta.tokensDeducted} tok · {meta.tokensRemaining}{" "}
              {t("storiesCreator.render.remaining", "pozostało")}
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {frames.map((f) => (
            <a
              key={f.index}
              href={f.signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block relative group rounded-lg overflow-hidden border border-white/[0.08] hover:border-[#ef3e29]/50 transition-all"
              data-testid={`rendered-frame-${f.index}`}
            >
              <img
                src={f.signedUrl}
                alt={`Frame ${f.index}`}
                className="w-full aspect-[9/16] object-cover"
              />
              <div className="absolute top-1 left-1 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase">
                {f.index}/{frames.length}
              </div>
            </a>
          ))}
        </div>
        <button
          onClick={downloadAll}
          className="flex items-center gap-1.5 text-[11px] bg-white/[0.06] hover:bg-white/[0.1] text-white/80 px-3 py-1.5 rounded-md border border-white/[0.08]"
          data-testid="btn-download-all-frames"
        >
          <Download size={12} />
          {t("storiesCreator.render.downloadAll", "Pobierz wszystkie PNG")}
        </button>
        {error && <div className="text-red-400 text-[10px]">{error}</div>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <button
        onClick={handleRender}
        disabled={loading}
        className="flex items-center justify-center gap-1.5 bg-[#ef3e29] hover:bg-[#dc2f1c] text-white text-[12px] font-semibold px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
        data-testid="btn-render-stories"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        {loading
          ? t("storiesCreator.render.generating", "Generuję grafiki…")
          : t("storiesCreator.render.cta", "Wygeneruj grafiki Stories")}
      </button>
      {loading && (
        <div className="w-full bg-white/[0.06] rounded-full h-1 overflow-hidden">
          <div
            className="bg-[#ef3e29] h-1 rounded-full transition-all duration-500"
            style={{ width: `${Math.max(progress, 2)}%` }}
          />
        </div>
      )}
      {error && <div className="text-red-400 text-[10px]">{error}</div>}
    </div>
  );
}
